import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "@i18n";
import IconComponent from "@CubeComponents/icons";
import UserNameTagComponent from "../../../cubeComponent/html/UserNameTagComponent";
import MenuComponent from "@CubeComponents/html/MenuComponent";
import MenuItemComponent from "@CubeComponents/html/MenuItemComponent";
import { uploadStringToTemp } from "@Services/tempUpload";
import { usePortalChat, messageTime, dayLabel } from "./context";
import { useChatComposer } from "../core/useChatComposer";
import {
  systemLineText,
  canEditMessage,
  everyoneReadUpTo,
  tickStateFor,
  isGroupedWithPrevious,
  showDaySeparator,
  typingLineText,
  conversationSubtitle,
} from "../core/chatThreadModel";
import { MESSAGE_TEXT_MAX_LENGTH, HIGHLIGHT_FLASH_MS, HIGHLIGHT_FETCH_BACK_MAX_PAGES } from "../portalChat.constants";
import type { ChatMessage } from "../api/portalChat.api";
import { EmojiPicker, QUICK_EMOJIS } from "./EmojiPicker";
import { MembersDialog, AttachmentsDialog, RenameDialog } from "./ThreadDialogs";
import { HideConfirmDialog } from "./ChatListDialogs";

function SystemLine({ message, selfId }: { readonly message: ChatMessage; readonly selfId: string }) {
  const { t } = useTranslation();
  // PRD §4.3 audience-aware wording lives in the shared thread model (mobile renders it too).
  const text = systemLineText(message, selfId, t);
  return (
    <div className="my-2 flex justify-center">
      <span className="rounded-full bg-[#f0f5f9] px-3 py-0.5 text-xs text-gray-500">{text}</span>
    </div>
  );
}

/** Right pane: thread header, message history, and the composer. */
function ChatThreadPane() {
  const { t } = useTranslation();
  const chat = usePortalChat();
  const conversation = chat.conversations.find((c) => c._id === chat.selectedId) ?? null;
  const thread = chat.thread;

  const [menuFor, setMenuFor] = useState<{ message: ChatMessage; anchor: HTMLElement } | null>(null);
  const [emojiAnchor, setEmojiAnchor] = useState<{ anchor: HTMLElement; messageId: string | null } | null>(null);
  const [showMembers, setShowMembers] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);
  const [showRename, setShowRename] = useState(false);
  const [threadMenuAnchor, setThreadMenuAnchor] = useState<HTMLElement | null>(null);
  const [hideCandidate, setHideCandidate] = useState(false);

  // PRD §4.6: open menus/popovers close when the panel is maximised or restored.
  useEffect(() => {
    setMenuFor(null);
    setEmojiAnchor(null);
    setThreadMenuAnchor(null);
  }, [chat.menuEpoch]);

  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const stickToBottomRef = useRef(true);

  // The composer state machine (draft, uploads, mentions, typing throttle, failed-send) is the
  // shared core; this pane supplies the web uploader and re-arms stick-to-bottom on send.
  const {
    text,
    pending,
    showMentionPicker,
    setShowMentionPicker,
    sending,
    uploading,
    failed,
    failedSend,
    addFiles,
    retryUpload,
    removePending,
    handleSend,
    retryFailedSend,
    handleTextChange,
    insertMention,
    insertText,
    dismissFailedSend,
  } = useChatComposer<File>({
    conversationId: chat.selectedId,
    memberCount: chat.thread?.members.length ?? 0,
    upload: async (file) => (await uploadStringToTemp(file, file.name)) ?? null,
    onSendStarted: () => {
      stickToBottomRef.current = true;
    },
  });

  const messages = thread?.messages ?? [];
  const lastMessageId = messages.at(-1)?._id ?? null;

  // Auto-scroll on new messages while the user is at (or near) the bottom.
  useEffect(() => {
    if (!scrollerRef.current || !stickToBottomRef.current) return;
    scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [lastMessageId, chat.selectedId, thread?.loading]);

  // Transient highlight for reply/forward click-through (PRD). A target older than the loaded
  // window pages backwards (bounded) until it appears; a target that no longer exists clears the
  // highlight with a toast instead of staying silently armed.
  const { highlightMessageId, setHighlightMessageId } = chat;
  const highlightFetchBackRef = useRef(0);
  const scrolledForHighlightRef = useRef<string | null>(null);
  useEffect(() => {
    highlightFetchBackRef.current = 0;
    scrolledForHighlightRef.current = null;
  }, [highlightMessageId]);
  useEffect(() => {
    if (!highlightMessageId) return undefined;
    // Scroll + arm the flash ONCE per target; live appends must not re-yank the scroll position
    // or restart the timer while the highlight is showing.
    if (scrolledForHighlightRef.current === highlightMessageId) return undefined;
    const element = document.getElementById(`portal-chat-msg-${highlightMessageId}`);
    if (element) {
      scrolledForHighlightRef.current = highlightMessageId;
      element.scrollIntoView({ block: "center", behavior: "smooth" });
      element.classList.add("bg-[#fff8dd]");
      const timer = setTimeout(() => {
        element.classList.remove("bg-[#fff8dd]");
        setHighlightMessageId(null);
      }, HIGHLIGHT_FLASH_MS);
      return () => clearTimeout(timer);
    }
    if (thread && !thread.loading && thread.hasMore && highlightFetchBackRef.current < HIGHLIGHT_FETCH_BACK_MAX_PAGES) {
      highlightFetchBackRef.current += 1;
      void chat.loadOlderMessages();
    } else if (thread && !thread.loading) {
      setHighlightMessageId(null);
      chat.showToast(t("This message is no longer available"), false);
    }
    return undefined;
    // eslint-disable-next-line react-hooks-ext/exhaustive-deps -- loadOlderMessages/showToast are stable core callbacks; messages.length re-arms the search per fetched page
  }, [highlightMessageId, setHighlightMessageId, messages.length, thread?.loading, thread?.hasMore]);

  const memberById = useMemo(() => {
    const map = new Map<string, string>();
    for (const member of thread?.members ?? []) map.set(String(member._id), member.fullName);
    return map;
  }, [thread?.members]);

  const readUpTo = useMemo(
    () => everyoneReadUpTo(thread?.members ?? [], thread?.memberReadDates ?? [], chat.userId),
    [thread?.members, thread?.memberReadDates, chat.userId],
  );

  if (!conversation) {
    // PRD §4.16: distinct copy for the unread filter, a brand-new portal, and a plain no-pick.
    const nothingExists = !chat.conversations.length;
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 text-center text-gray-400">
        <IconComponent name="Chat" width="3rem" height="3rem" />
        {chat.unreadOnly ? (
          <>
            <span className="text-sm font-semibold">{t("No unread messages")}</span>
            <span className="text-xs">
              {t("Nothing here is waiting on you. Switch off Unread to reopen your conversations.")}
            </span>
          </>
        ) : nothingExists ? (
          <>
            <span className="text-sm font-semibold">{t("No conversation created yet")}</span>
            <span className="text-xs">
              {t("Pick a way to start on the left. Once a conversation exists, it opens here.")}
            </span>
          </>
        ) : (
          <span className="text-sm">{t("Select a chat to start messaging")}</span>
        )}
      </div>
    );
  }

  const subtitle = conversationSubtitle(conversation, t);

  const renderTicks = (message: ChatMessage) => {
    const tick = tickStateFor(message, chat.userId, readUpTo);
    if (!tick) return null;
    const read = tick === "read";
    return (
      <span className={`ms-1 text-[0.6rem] ${read ? "text-[#1E88E5]" : "text-gray-400"}`}>{read ? "✓✓" : "✓"}</span>
    );
  };

  const renderMessage = (message: ChatMessage, index: number) => {
    const previous = index > 0 ? messages[index - 1] : undefined;
    const own = String(message.senderId) === chat.userId;
    const showDay = showDaySeparator(message, previous);
    const grouped = isGroupedWithPrevious(message, previous);

    if (message.kind === "system") {
      return (
        <div key={message._id}>
          {showDay && (
            <div className="my-3 flex justify-center">
              <span className="rounded-full bg-white px-3 py-0.5 text-xs font-semibold text-gray-400 shadow-sm">
                {dayLabel(message.createdDate, t("Today"), t("Yesterday"))}
              </span>
            </div>
          )}
          <SystemLine message={message} selfId={chat.userId} />
        </div>
      );
    }

    const forward = message.forwardOf;
    const reply = message.replyTo;

    return (
      <div key={message._id} id={`portal-chat-msg-${message._id}`} className="rounded transition-colors duration-500">
        {showDay && (
          <div className="my-3 flex justify-center">
            <span className="rounded-full bg-white px-3 py-0.5 text-xs font-semibold text-gray-400 shadow-sm">
              {dayLabel(message.createdDate, t("Today"), t("Yesterday"))}
            </span>
          </div>
        )}
        <div className={`group flex gap-2 px-4 ${grouped ? "mt-0.5" : "mt-3"} ${own ? "flex-row-reverse" : ""}`}>
          {!own && !grouped ? (
            <UserNameTagComponent showAvatarOnly id={String(message.senderId)} size="XS" isCircle />
          ) : (
            !own && <span className="w-8" />
          )}
          <div className={`max-w-[70%] ${own ? "items-end" : ""}`}>
            {!grouped && !own && <div className="mb-0.5 text-xs font-semibold text-gray-600">{message.senderName}</div>}
            <div
              className={`relative rounded-lg border px-3 py-2 text-sm ${
                own ? "border-[#cfe3f7] bg-[#e8f1fc]" : "border-[#eceff4] bg-white"
              }`}
            >
              {message.deleted ? (
                <span className="italic text-gray-400">{t("This message was deleted")}</span>
              ) : (
                <>
                  {message.text && forward && (
                    <div className="mb-1 whitespace-pre-wrap break-words">{message.text}</div>
                  )}
                  {reply && (
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => chat.jumpToMessage(conversation._id, String(reply.messageId))}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") chat.jumpToMessage(conversation._id, String(reply.messageId));
                      }}
                      className="mb-1 cursor-pointer rounded border-s-2 border-[#6BBBE3] bg-[#f6fafd] px-2 py-1 text-xs"
                    >
                      <div className="font-semibold text-[#1E88E5]">{reply.senderName}</div>
                      <div className="truncate text-gray-500">
                        {reply.deleted ? t("This message is no longer available") : reply.text || t("Attachment")}
                      </div>
                    </div>
                  )}
                  {forward && (
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        // PRD §4.4: a deleted source still navigates — the viewer lands on the
                        // source conversation with a "no longer available" note instead of a highlight.
                        if (!forward.sourceAccessible) return;
                        if (forward.sourceDeleted) {
                          chat.selectConversation(String(forward.conversationId));
                          chat.showToast(t("This message is no longer available"), false);
                        } else {
                          chat.jumpToMessage(String(forward.conversationId), String(forward.messageId));
                        }
                      }}
                      onKeyDown={() => undefined}
                      className={`mb-1 rounded border border-dashed border-[#c6d8ec] bg-[#f9fbfe] px-2 py-1 text-xs ${
                        forward.sourceAccessible ? "cursor-pointer" : ""
                      }`}
                    >
                      {forward.note && (
                        <div className="mb-1 border-b border-[#e3ecf5] pb-1">
                          <span className="font-semibold">{forward.noteAuthorName}: </span>
                          <span className="whitespace-pre-wrap break-words">{forward.note}</span>
                        </div>
                      )}
                      <div className="mt-0.5 flex items-start gap-1.5">
                        <UserNameTagComponent showAvatarOnly id={String(forward.senderId)} size="XS" isCircle />
                        <span className="min-w-0">
                          <span className="font-semibold">{forward.senderName}: </span>
                          <span className="whitespace-pre-wrap break-words">{forward.text}</span>
                        </span>
                      </div>
                      {forward.attachments?.map((attachment) => (
                        <div key={attachment.awsKey} className="mt-0.5 text-[#1E88E5]">
                          📎 {attachment.attachmentsData.name}
                        </div>
                      ))}
                      <div className="mt-1 flex items-center gap-1 text-gray-500">
                        <IconComponent name="ForwardArrow" width="0.75rem" height="0.75rem" />
                        {forward.sourceAccessible
                          ? t("Forwarded from {{name}}", { name: forward.sourceName ?? "" })
                          : t("Forwarded from a private chat")}
                      </div>
                    </div>
                  )}
                  {message.text && !forward && <span className="whitespace-pre-wrap break-words">{message.text}</span>}
                  {message.attachments?.map((attachment) => (
                    <span key={attachment.awsKey} className="mt-1 flex items-center gap-1 text-xs">
                      <a
                        href={attachment.downloadLink}
                        target="_blank"
                        rel="noreferrer"
                        className="flex min-w-0 items-center gap-1 text-[#1E88E5] hover:underline"
                      >
                        <IconComponent name="attachFile" width="0.8rem" height="0.8rem" />
                        <span className="truncate">{attachment.attachmentsData.name}</span>
                      </a>
                      {own && (
                        <span
                          role="button"
                          tabIndex={-1}
                          className="invisible group-hover:visible"
                          title={t("Remove attachment")}
                          onClick={(e) => {
                            e.stopPropagation();
                            void chat.deleteAttachment(message._id, attachment.awsKey);
                          }}
                          onKeyDown={() => undefined}
                        >
                          <IconComponent name="CrossIcon" width="0.6rem" height="0.6rem" />
                        </span>
                      )}
                    </span>
                  ))}
                </>
              )}
              <div className="mt-1 flex items-center justify-end gap-1 text-[0.6rem] text-gray-400">
                {message.editedDate && !message.deleted && <span>({t("edited")})</span>}
                <span>{messageTime(message.createdDate)}</span>
                {renderTicks(message)}
              </div>
              {!message.deleted && (
                <div
                  className={`absolute -top-3 hidden items-center gap-0.5 rounded-full border border-[#e3ecf5] bg-white px-1 shadow-sm group-hover:flex ${
                    own ? "start-2" : "end-2"
                  }`}
                >
                  {QUICK_EMOJIS.slice(0, 3).map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      className="p-0.5 text-xs hover:scale-125"
                      onClick={() => void chat.toggleReaction(message._id, emoji)}
                    >
                      {emoji}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="p-0.5 text-xs"
                    onClick={(e) => setEmojiAnchor({ anchor: e.currentTarget, messageId: message._id })}
                  >
                    ➕
                  </button>
                  <span
                    role="button"
                    tabIndex={-1}
                    onClick={(e) => setMenuFor({ message, anchor: e.currentTarget })}
                    onKeyDown={() => undefined}
                  >
                    <IconComponent name="threedots" width="0.9rem" height="0.9rem" />
                  </span>
                </div>
              )}
            </div>
            {!message.deleted && (message.reactions?.length ?? 0) > 0 && (
              <div className={`mt-0.5 flex flex-wrap gap-1 ${own ? "justify-end" : ""}`}>
                {(message.reactions ?? []).map((reaction) => {
                  const mine = reaction.users.some((u) => String(u) === chat.userId);
                  const names = reaction.users
                    .map((u) => (String(u) === chat.userId ? t("You") : (memberById.get(String(u)) ?? "")))
                    .filter(Boolean)
                    .join(", ");
                  return (
                    <button
                      key={reaction.emoji}
                      type="button"
                      title={names}
                      onClick={() => {
                        if (mine) void chat.toggleReaction(message._id, reaction.emoji);
                        else chat.openReactionsDialog(message);
                      }}
                      className={`rounded-full border px-1.5 text-xs ${
                        mine ? "border-[#6BBBE3] bg-[#e8f1fc]" : "border-[#e3ecf5] bg-white"
                      }`}
                    >
                      {reaction.emoji} {reaction.users.length}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const typingLine = typingLineText(chat.typingUsers, t);

  const intent = chat.composerIntent;

  return (
    <div className="flex min-w-0 flex-1 flex-col">
      {/* Thread header */}
      <div className="flex items-center justify-between border-b border-[#e8f1fc] px-4 py-2">
        <div className="flex min-w-0 items-center gap-2">
          {conversation.type === "direct" && conversation.otherUser ? (
            <UserNameTagComponent showAvatarOnly id={String(conversation.otherUser._id)} size="XS" isCircle />
          ) : (
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#e8f1fc]">
              <IconComponent
                name={conversation.type === "space" ? "Grouping" : "usersIcon"}
                width="1rem"
                height="1rem"
              />
            </span>
          )}
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold">{conversation.name}</div>
            {subtitle && <div className="truncate text-xs text-gray-500">{subtitle}</div>}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <IconComponent
            name="Volume1D"
            width="1rem"
            height="1rem"
            toolTipContent={conversation.muted ? t("Unmute") : t("Mute")}
            className={conversation.muted ? "opacity-100" : "opacity-40"}
            onClick={() => void chat.updateConversationState(conversation._id, { muted: !conversation.muted })}
          />
          <IconComponent
            name="attachFile"
            width="1rem"
            height="1rem"
            toolTipContent={t("View attachments")}
            onClick={() => setShowAttachments(true)}
          />
          {conversation.type !== "direct" && (
            <IconComponent
              name="usersIcon"
              width="1.1rem"
              height="1.1rem"
              toolTipContent={t("Members")}
              onClick={() => setShowMembers(true)}
            />
          )}
          <span
            role="button"
            tabIndex={-1}
            onClick={(e) => setThreadMenuAnchor(e.currentTarget)}
            onKeyDown={() => undefined}
          >
            <IconComponent name="threedots" width="1rem" height="1rem" toolTipContent={t("More")} />
          </span>
        </div>
      </div>
      {threadMenuAnchor && (
        <MenuComponent anchorEl={threadMenuAnchor} open onClose={() => setThreadMenuAnchor(null)} width="14rem">
          <MenuItemComponent
            onClick={() => {
              setThreadMenuAnchor(null);
              setHideCandidate(true);
            }}
          >
            {t("Hide Conversation")}
          </MenuItemComponent>
          {conversation.type === "group" && (
            <MenuItemComponent
              onClick={() => {
                setThreadMenuAnchor(null);
                setShowRename(true);
              }}
            >
              {t("Rename Conversation")}
            </MenuItemComponent>
          )}
          <MenuItemComponent
            onClick={() => {
              setThreadMenuAnchor(null);
              setShowAttachments(true);
            }}
          >
            {t("View attachments")}
          </MenuItemComponent>
          {conversation.type !== "direct" && (
            <MenuItemComponent
              onClick={() => {
                setThreadMenuAnchor(null);
                setShowMembers(true);
              }}
            >
              {t("Members")}
            </MenuItemComponent>
          )}
        </MenuComponent>
      )}

      {/* Messages */}
      <div
        ref={scrollerRef}
        onScroll={(e) => {
          const el = e.currentTarget;
          stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
        }}
        className="min-h-0 flex-1 overflow-y-auto bg-[#f7fafc] pb-3"
      >
        {thread?.loading && <div className="p-4 text-sm text-gray-500">{t("Loading")}…</div>}
        {thread && !thread.loading && thread.error && (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-gray-500">
            <span className="text-sm font-semibold">{t("Couldn't load this conversation")}</span>
            <span className="text-xs">{t("Check your connection and try again.")}</span>
            <button
              type="button"
              className="rounded border border-[#dce6f1] px-3 py-1 text-xs text-[#1E88E5]"
              onClick={() => chat.selectConversation(conversation._id)}
            >
              {t("Retry")}
            </button>
          </div>
        )}
        {thread && !thread.loading && thread.hasMore && (
          <div className="flex justify-center pt-2">
            <button
              type="button"
              className="rounded-full border border-[#dce6f1] bg-white px-3 py-0.5 text-xs text-[#1E88E5]"
              onClick={() => void chat.loadOlderMessages()}
            >
              {t("Load earlier messages")}
            </button>
          </div>
        )}
        {thread && !thread.loading && !thread.error && !messages.length && (
          <div className="flex h-full flex-col items-center justify-center gap-1 px-6 text-center text-gray-400">
            {conversation.type === "space" && (
              <span className="text-sm">
                {t(
                  "Nothing has been posted in the {{name}} Space yet. Start the conversation and everyone in this Space will see it.",
                  { name: conversation.name },
                )}
              </span>
            )}
            {conversation.type === "direct" && (
              <span className="text-sm">
                {t("Say hello to {{name}} to start this conversation.", {
                  name: conversation.name.split(" ")[0] ?? conversation.name,
                })}
              </span>
            )}
            {conversation.type === "group" && (
              <span className="text-sm">
                {t("No one has posted in {{name}} yet. Start the conversation.", { name: conversation.name })}
              </span>
            )}
          </div>
        )}
        {messages.map((message, index) => renderMessage(message, index))}
        {failedSend && failedSend.conversationId === conversation._id && (
          <div className="mt-3 flex justify-end px-4">
            <div className="max-w-[70%] rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm">
              {failedSend.text && <span className="whitespace-pre-wrap break-words">{failedSend.text}</span>}
              {failedSend.attachments.map((attachment) => (
                <div key={attachment.key} className="text-xs text-gray-500">
                  📎 {attachment.name}
                </div>
              ))}
              <div className="mt-1 flex items-center justify-end gap-2 text-xs">
                <span className="text-red-500">{t("Not sent.")}</span>
                <button type="button" className="text-[#1E88E5]" onClick={() => void retryFailedSend()}>
                  {t("Retry")}
                </button>
                <button type="button" className="text-gray-400" onClick={dismissFailedSend}>
                  {t("Remove")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Typing indicator */}
      {typingLine && <div className="px-4 py-0.5 text-xs italic text-gray-500">{typingLine}</div>}

      {/* Composer */}
      <div className="border-t border-[#e8f1fc] p-3">
        {!chat.connected && (
          <div className="mb-2 rounded bg-[#fff3e0] px-2 py-1 text-xs text-[#b26a00]">
            {t("You're offline. New messages will appear and sending is paused until you're back.")}
          </div>
        )}
        {intent && (
          <div className="mb-2 flex items-center justify-between rounded border-s-2 border-[#6BBBE3] bg-[#f6fafd] px-2 py-1 text-xs">
            <div className="min-w-0">
              <div className="font-semibold text-[#1E88E5]">
                {t("Replying to {{name}}", { name: intent.message.senderName })}
              </div>
              <div className="truncate text-gray-500">{intent.message.text ?? t("Attachment")}</div>
            </div>
            <span role="button" tabIndex={0} onClick={() => chat.setComposerIntent(null)} onKeyDown={() => undefined}>
              <IconComponent name="CrossIcon" width="0.8rem" height="0.8rem" />
            </span>
          </div>
        )}
        {pending.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-2">
            {pending.map((item) => (
              <span
                key={item.id}
                className={`flex items-center gap-1 rounded border px-2 py-0.5 text-xs ${
                  item.status === "error" ? "border-red-300 bg-red-50" : "border-[#dce6f1] bg-[#f6fafd]"
                }`}
              >
                <IconComponent name="attachFile" width="0.7rem" height="0.7rem" />
                <span className="max-w-[10rem] truncate">{item.file.name}</span>
                {item.status === "uploading" && <span className="text-gray-400">…</span>}
                {item.status === "error" && (
                  <button type="button" className="text-[#1E88E5]" onClick={() => void retryUpload(item)}>
                    {t("Retry")}
                  </button>
                )}
                <span role="button" tabIndex={0} onClick={() => removePending(item.id)} onKeyDown={() => undefined}>
                  <IconComponent name="CrossIcon" width="0.6rem" height="0.6rem" />
                </span>
              </span>
            ))}
          </div>
        )}
        <div className="flex items-end gap-2">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={(e) => {
              void addFiles(Array.from(e.target.files ?? []));
              e.target.value = "";
            }}
          />
          <IconComponent
            name="attachFile"
            width="1.2rem"
            height="1.2rem"
            toolTipContent={t("Attach files")}
            onClick={() => fileInputRef.current?.click()}
          />
          <button
            type="button"
            className="text-lg"
            title={t("Emoji")}
            onClick={(e) => setEmojiAnchor({ anchor: e.currentTarget, messageId: null })}
          >
            🙂
          </button>
          <div className="relative min-w-0 flex-1">
            <textarea
              ref={textareaRef}
              value={text}
              disabled={!chat.connected}
              rows={Math.min(5, Math.max(1, text.split("\n").length))}
              maxLength={MESSAGE_TEXT_MAX_LENGTH}
              placeholder={t("Type a message")}
              onChange={(e) => handleTextChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void handleSend();
                }
                if (e.key === "Escape") {
                  if (showMentionPicker) setShowMentionPicker(false);
                  else chat.setComposerIntent(null);
                }
              }}
              className="w-full resize-none rounded border border-[#dce6f1] px-2 py-1.5 text-sm outline-none focus:border-[#6BBBE3]"
            />
            {text.length > MESSAGE_TEXT_MAX_LENGTH - 200 && (
              <span className="absolute -top-4 end-0 text-[0.6rem] text-gray-400">
                {text.length}/{MESSAGE_TEXT_MAX_LENGTH}
              </span>
            )}
            {showMentionPicker && (
              <div className="absolute bottom-full start-0 z-10 mb-1 max-h-40 w-64 overflow-y-auto rounded border border-[#dce6f1] bg-white shadow-lg">
                {(thread?.members ?? [])
                  .filter((member) => String(member._id) !== chat.userId)
                  .map((member) => (
                    <button
                      key={String(member._id)}
                      type="button"
                      className="flex w-full items-center gap-2 px-2 py-1 text-start text-sm hover:bg-[#f0f5f9]"
                      onClick={() => {
                        insertMention({ _id: String(member._id), fullName: member.fullName });
                        textareaRef.current?.focus();
                      }}
                    >
                      <UserNameTagComponent showAvatarOnly id={String(member._id)} size="XS" isCircle />
                      {member.fullName}
                    </button>
                  ))}
                <button
                  type="button"
                  className="w-full px-2 py-1 text-start text-xs text-gray-400"
                  onClick={() => setShowMentionPicker(false)}
                >
                  {t("Close")}
                </button>
              </div>
            )}
          </div>
          <button
            type="button"
            disabled={sending || uploading || failed || !chat.connected}
            onClick={() => void handleSend()}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-[#6BBBE3] disabled:opacity-40"
            title={
              !chat.connected
                ? t("Reconnecting…")
                : uploading
                  ? t("Waiting for uploads to finish")
                  : failed
                    ? t("Retry or remove the failed attachment first")
                    : t("Send")
            }
          >
            <IconComponent name="send" width="1rem" height="1rem" />
          </button>
        </div>
      </div>

      {/* Message action menu */}
      {menuFor && (
        <MenuComponent anchorEl={menuFor.anchor} open onClose={() => setMenuFor(null)} width="13rem">
          <MenuItemComponent
            onClick={() => {
              chat.setComposerIntent({ kind: "reply", message: menuFor.message });
              setMenuFor(null);
              textareaRef.current?.focus();
            }}
          >
            {t("Reply")}
          </MenuItemComponent>
          <MenuItemComponent
            onClick={() => {
              chat.openForwardDialog(menuFor.message);
              setMenuFor(null);
            }}
          >
            {t("Forward")}
          </MenuItemComponent>
          <MenuItemComponent
            onClick={() => {
              void navigator.clipboard.writeText(menuFor.message.text ?? "");
              setMenuFor(null);
            }}
          >
            {t("Copy text")}
          </MenuItemComponent>
          {canEditMessage(menuFor.message, chat.userId) && (
            <MenuItemComponent
              onClick={() => {
                chat.openEditDialog(menuFor.message);
                setMenuFor(null);
              }}
            >
              {t("Edit message")}
            </MenuItemComponent>
          )}
          {String(menuFor.message.senderId) === chat.userId && (
            <MenuItemComponent
              onClick={() => {
                void chat.deleteMessage(menuFor.message._id);
                setMenuFor(null);
              }}
            >
              {t("Delete")}
            </MenuItemComponent>
          )}
          {(menuFor.message.reactions?.length ?? 0) > 0 && (
            <MenuItemComponent
              onClick={() => {
                chat.openReactionsDialog(menuFor.message);
                setMenuFor(null);
              }}
            >
              {t("View reactions")}
            </MenuItemComponent>
          )}
          <MenuItemComponent
            onClick={() => {
              chat.openReceiptsDialog(menuFor.message);
              setMenuFor(null);
            }}
          >
            {t("Read receipts")}
          </MenuItemComponent>
        </MenuComponent>
      )}

      {/* Emoji picker: reaction target or composer insertion */}
      {emojiAnchor && (
        <EmojiPicker
          anchorEl={emojiAnchor.anchor}
          onClose={() => setEmojiAnchor(null)}
          onPick={(emoji) => {
            if (emojiAnchor.messageId) void chat.toggleReaction(emojiAnchor.messageId, emoji);
            else insertText(emoji);
          }}
        />
      )}

      {showMembers && <MembersDialog conversation={conversation} onClose={() => setShowMembers(false)} />}
      {showAttachments && <AttachmentsDialog conversation={conversation} onClose={() => setShowAttachments(false)} />}
      {showRename && <RenameDialog conversation={conversation} onClose={() => setShowRename(false)} />}
      {hideCandidate && <HideConfirmDialog conversation={conversation} onClose={() => setHideCandidate(false)} />}
    </div>
  );
}

export default ChatThreadPane;
